/**
 * TemplateComposer — compose or edit a message template.
 *
 * Recipients are worked out per message from the records: the author picks
 * WHERE an address comes from (a master looked up from the record, a column of
 * the record, a fixed address, everyone holding a role, the record's creator)
 * — never types the addresses of the people a send will reach. Per channel:
 * the email's subject + rich body (merge fields, repeats) + included R/V/D,
 * WhatsApp's approved template + variables, Telegram / SMS text.
 *
 * Everything offered comes from the send's BINDING (its channels, attachables
 * and lookup masters) and the object's fields; the server validates the saved
 * template against the same binding (`validate_template`), so nothing the
 * composer could be tricked into writing reaches further than the button.
 *
 * Used three ways: the Run view's send window ("New" / "Edit"), the tenant
 * admin's Templates tab, and the designer's "+ New starter template".
 */
import { useCallback, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Loader2, Plus, Search, Trash2 } from 'lucide-react'
import MessageBodyEditor, { type MessageBodyEditorHandle } from './MessageBodyEditor'
import {
  CHANNEL_LABELS, CHANNELS, GROUPING_LABELS, KIND_LABELS, MODE_LABELS, describeRecipient, emptyTemplate, slugId,
  type ChannelKey, type DeliveryMode, type EmailBlock, type GroupingMode, type MessageTemplate, type PmDoc,
  type RecipientRole, type RecipientSpec, type SendActionBinding, type SendFields, type TemplateAttachment,
  type TextBlock,
} from './types'
import { ErrorNote, Field, Note, Seg, TabBar, input, label as labelStyle, meta, mono, textarea } from './ui'

export type ComposerScope = 'personal' | 'shared' | 'override' | 'starter'

type SourceKind = 'lookup' | 'column' | 'typed' | 'role_users' | 'record_owner' | 'user'

const SOURCE_LABELS: Record<SourceKind, string> = {
  lookup: 'Master lookup', column: 'This record', typed: 'Fixed', role_users: 'Role', record_owner: 'Creator',
  user: 'A person',
}

/** Text-channel modes (a document, or a link). */
const TEXT_MODES: DeliveryMode[] = ['pdf', 'link']

function sourceKind(r: RecipientSpec): SourceKind {
  const f = r.from as Record<string, unknown>
  if ('lookup' in f) return 'lookup'
  if ('typed' in f) return 'typed'
  if ('role_users' in f) return 'role_users'
  if ('record_owner' in f) return 'record_owner'
  if ('user' in f) return 'user'
  return 'column'
}

function cloneTpl(t: MessageTemplate): MessageTemplate {
  return JSON.parse(JSON.stringify(t)) as MessageTemplate
}

function emptyBlock(ch: ChannelKey): EmailBlock | TextBlock {
  if (ch === 'email') return { subject: '', body: { type: 'doc', content: [{ type: 'paragraph' }] }, attachments: [] }
  if (ch === 'whatsapp') return { text: '', attachments: [], template_name: '', variables: [] }
  return { text: '', attachments: [] }
}

/** The GSM-7 basic set (one SMS part = 160 of these; anything else → 70). */
const GSM7 = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\[~\]|€]*$/

type FocusTarget =
  | { kind: 'body' }
  | { kind: 'input'; el: HTMLInputElement | HTMLTextAreaElement; set: (v: string) => void }

export function TemplateComposer({
  objName, binding, fields, initial, scope: initialScope, canShare, mode = 'runtime',
  onSave, onCancel, saveAndPreviewLabel,
}: {
  objName: string
  binding: SendActionBinding
  fields: SendFields
  /** The template being edited; null = a new one. */
  initial: MessageTemplate | null
  /** Where an EDITED template is saved (a starter edited by a tenant = override). */
  scope?: ComposerScope
  /** May save for everyone in the organization (`send_templates`). */
  canShare: boolean
  /** design = the designer's starter templates (no scope choice). */
  mode?: 'runtime' | 'admin' | 'design'
  /** Persist; resolves with the saved template. `andPreview` = the caller
   *  should go straight to the preview with it. */
  onSave: (tpl: MessageTemplate, scope: ComposerScope, andPreview: boolean) => Promise<void>
  onCancel: () => void
  saveAndPreviewLabel?: string
}) {
  const allowed = useMemo(() => CHANNELS.filter((c) => (binding.channels || CHANNELS).includes(c)), [binding.channels])
  const [tpl, setTpl] = useState<MessageTemplate>(() => {
    const t = initial ? cloneTpl(initial) : emptyTemplate(objName)
    if (!t.channels || !Object.keys(t.channels).length) t.channels = { email: emptyBlock('email') as EmailBlock }
    return t
  })
  const isNew = !initial
  const [scope, setScope] = useState<ComposerScope>(
    initialScope || (mode === 'design' ? 'starter' : 'personal'))
  const [tab, setTab] = useState<ChannelKey>(() =>
    (allowed.find((c) => tpl.channels[c]) || allowed[0] || 'email'))
  const [sel, setSel] = useState<number>(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ message: string; problems: string[] } | null>(null)
  const [q, setQ] = useState('')
  const bodyRef = useRef<MessageBodyEditorHandle>(null)
  const focusRef = useRef<FocusTarget>({ kind: 'body' })

  const patch = useCallback((fn: (t: MessageTemplate) => void) => {
    setTpl((prev) => {
      const next = cloneTpl(prev)
      fn(next)
      return next
    })
  }, [])

  // ── field insertion into whichever editor had focus ────────────────────
  const insert = useCallback((key: string) => {
    const f = focusRef.current
    if (f.kind === 'input' && f.el.isConnected) {
      const el = f.el
      const start = el.selectionStart ?? el.value.length
      const end = el.selectionEnd ?? start
      const token = `{${key}}`
      const next = el.value.slice(0, start) + token + el.value.slice(end)
      f.set(next)
      requestAnimationFrame(() => {
        try {
          el.focus()
          el.setSelectionRange(start + token.length, start + token.length)
        } catch { /* the field re-rendered — the value is in place */ }
      })
      return
    }
    if (tab === 'email') bodyRef.current?.insertField(key)
  }, [tab])

  const track = (set: (v: string) => void) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      focusRef.current = { kind: 'input', el: e.currentTarget, set }
    },
  })

  // ── recipients ─────────────────────────────────────────────────────────
  const recipients = tpl.recipients || []
  const cur = recipients[sel]
  const lookups = binding.lookups || []
  const headerCols = fields.header

  const addRecipient = () => {
    const ch: ChannelKey = allowed.includes(tab) ? tab : (allowed[0] || 'email')
    const r: RecipientSpec = lookups.length
      ? { role: 'to', channel: ch, from: { lookup: lookups[0], match: {}, column: '' } }
      : { role: 'to', channel: ch, from: { column: headerCols[0]?.key || '' } }
    patch((t) => { t.recipients = [...(t.recipients || []), r] })
    setSel(recipients.length)
  }

  const setRecipient = (i: number, r: RecipientSpec) => patch((t) => { t.recipients[i] = r })

  const setSource = (i: number, kind: SourceKind) => {
    const r = recipients[i]
    if (!r) return
    let from: RecipientSpec['from']
    if (kind === 'lookup') from = { lookup: lookups[0] || '', match: {}, column: '' }
    else if (kind === 'typed') from = { typed: '' }
    else if (kind === 'role_users') from = { role_users: '' }
    else if (kind === 'record_owner') from = { record_owner: true }
    else if (kind === 'user') from = { user: '' }
    else from = { column: headerCols[0]?.key || '' }
    setRecipient(i, { ...r, from })
  }

  // ── channel blocks ─────────────────────────────────────────────────────
  const block = (tpl.channels as Record<string, EmailBlock | TextBlock | undefined>)[tab]
  const ensureBlock = (ch: ChannelKey) => patch((t) => {
    const chans = t.channels as Record<string, EmailBlock | TextBlock>
    if (!chans[ch]) chans[ch] = emptyBlock(ch)
  })
  const setBlock = (fn: (b: EmailBlock & TextBlock) => void) => patch((t) => {
    const chans = t.channels as Record<string, EmailBlock | TextBlock>
    if (!chans[tab]) chans[tab] = emptyBlock(tab)
    fn(chans[tab] as EmailBlock & TextBlock)
  })

  const attachables = binding.attachables || []
  const addAttachment = () => {
    const a = attachables[0]
    if (!a) return
    const modes = tab === 'email' ? a.modes : a.modes.filter((m) => TEXT_MODES.includes(m))
    const att: TemplateAttachment = { ref: a.id, mode: modes[0] || 'pdf', per: 'record', filename: '' }
    setBlock((b) => { b.attachments = [...(b.attachments || []), att] })
  }

  // ── save ───────────────────────────────────────────────────────────────
  const save = async (andPreview: boolean) => {
    const name = (tpl.name || '').trim()
    if (!name) {
      setError({ message: 'Give the template a name.', problems: [] })
      return
    }
    const problems: string[] = []
    for (const r of recipients) {
      const f = r.from as Record<string, unknown>
      if ('lookup' in f && (!f.lookup || !f.column || !Object.keys((f.match as object) || {}).length)) {
        problems.push(`A ${CHANNEL_LABELS[r.channel]} recipient needs its master, the matching column and the column to use.`)
      }
      if ('column' in f && !('lookup' in f) && !f.column) problems.push('A recipient needs the record column to use.')
      if ('typed' in f && !String(f.typed || '').trim()) problems.push('A fixed recipient needs its address.')
      if ('role_users' in f && !String(f.role_users || '').trim()) problems.push('A role recipient needs the role.')
    }
    if (problems.length) {
      setError({ message: 'Some recipients are incomplete.', problems })
      return
    }
    const out = cloneTpl(tpl)
    out.name = name
    if (!out.id) out.id = slugId(name)
    out.obj_name = objName
    setBusy(true)
    setError(null)
    try {
      await onSave(out, scope, andPreview)
    } catch (e) {
      const any = e as { message?: string; problems?: string[] }
      setError({ message: any?.message || 'The template could not be saved.', problems: Array.isArray(any?.problems) ? any.problems : [] })
    } finally {
      setBusy(false)
    }
  }

  // ── fields panel ───────────────────────────────────────────────────────
  const fieldGroups = useMemo(() => {
    const groups: { title: string; items: { key: string; label: string; code: string }[] }[] = [
      { title: 'This record', items: fields.header.map((c) => ({ key: c.key, label: c.heading, code: c.key })) },
    ]
    for (const f of fields.forms) {
      groups.push({ title: `${f.title} (in a repeat)`, items: f.columns.map((c) => ({ key: c.key, label: c.heading, code: c.key })) })
    }
    for (const l of fields.lookups) {
      groups.push({ title: l.obj, items: l.columns.map((c) => ({ key: `lookup.${l.obj}.${c.key}`, label: c.heading, code: c.key })) })
    }
    const numeric = fields.header.filter((c) => /NUM|INT|DEC|FLOAT|DOUBLE|MONEY/.test(c.type))
    groups.push({
      title: 'Group',
      items: [...fields.group.map((t) => ({ key: t.key, label: t.heading, code: t.key })),
        ...numeric.map((c) => ({ key: `TOTAL.${c.key}`, label: `Total of ${c.heading}`, code: `TOTAL.${c.key}` }))],
    })
    groups.push({ title: 'System', items: fields.system.map((t) => ({ key: t.key, label: t.heading, code: t.key })) })
    const needle = q.trim().toLowerCase()
    if (!needle) return groups
    return groups.map((g) => ({ ...g, items: g.items.filter((i) => `${i.label} ${i.code}`.toLowerCase().includes(needle)) }))
      .filter((g) => g.items.length)
  }, [fields, q])

  const masterCols = (obj: string) => fields.lookups.find((l) => l.obj.toLowerCase() === obj.toLowerCase())?.columns || []

  const smsText = tab === 'sms' ? String((block as TextBlock | undefined)?.text || '') : ''
  const smsGsm = GSM7.test(smsText)
  const smsPer = smsGsm ? 160 : 70

  const scopeOptions: { value: ComposerScope; label: string }[] = mode === 'design' ? [] : (
    initialScope === 'override'
      ? [{ value: 'override', label: 'Everyone in my organization' }]
      : [{ value: 'personal', label: 'Only me' },
        ...(canShare ? [{ value: 'shared' as ComposerScope, label: 'Everyone in my organization' }] : [])])

  const groupingValue = tpl.grouping?.mode || ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 12, padding: '14px 20px', flexWrap: 'wrap',
        borderBottom: '1px solid var(--color-border, #d8dee6)',
      }}>
        <Field title="Template name" htmlFor="twa-tpl-name" style={{ flex: '1 1 260px' }}>
          <input id="twa-tpl-name" style={input} value={tpl.name}
                 onChange={(e) => { const v = e.target.value; patch((t) => { t.name = v }) }}
                 placeholder="e.g. Invoice mail — CEO copy" />
        </Field>
        {scopeOptions.length > 1 && (
          <Field title="Who can use it">
            <Seg ariaLabel="Who can use it" options={scopeOptions} value={scope} onChange={setScope} />
          </Field>
        )}
        <Field title="Group selected records" htmlFor="twa-tpl-grp">
          <select id="twa-tpl-grp" style={{ ...input, width: 240 }} value={groupingValue}
                  onChange={(e) => {
                    const v = e.target.value as GroupingMode | ''
                    patch((t) => { t.grouping = v ? { mode: v, by: v === 'per_entity' ? (binding.group_by || []) : [] } : null })
                  }}>
            <option value="">As the send is set up</option>
            <option value="per_entity">
              {GROUPING_LABELS.per_entity}{binding.group_by?.length ? ` (${binding.group_by.join(', ')})` : ''}
            </option>
            <option value="per_record">{GROUPING_LABELS.per_record}</option>
            <option value="single">{GROUPING_LABELS.single}</option>
          </select>
        </Field>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-secondary" onClick={() => void save(false)} disabled={busy}>
            {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />} Save
          </button>
          {saveAndPreviewLabel && (
            <button type="button" className="btn btn-primary" onClick={() => void save(true)} disabled={busy}>
              {saveAndPreviewLabel}
            </button>
          )}
        </div>
      </div>
      {error && <div style={{ padding: '10px 20px 0' }}><ErrorNote error={error} /></div>}
      {isNew && mode !== 'design' && scope === 'personal' && !canShare && (
        <div style={{ padding: '10px 20px 0' }}>
          <Note>Saved for you only. Sharing a template with everyone needs the “Save shared message templates” right.</Note>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1, minHeight: 0 }}>
        {/* ── Recipients ── */}
        <section style={{
          flex: '1 1 320px', maxWidth: 380, padding: 16, boxSizing: 'border-box', display: 'flex',
          flexDirection: 'column', gap: 10, borderRight: '1px solid var(--color-border, #e6eaef)',
          background: 'var(--color-bg-secondary, #fafbfc)',
        }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Recipients</h2>
          <p style={{ ...meta, margin: 0, lineHeight: 1.5 }}>
            Worked out per message from the records — the person sending never types an address.
          </p>
          {recipients.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button type="button" onClick={() => setSel(i)} style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', font: 'inherit',
                fontSize: 13, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', minWidth: 0,
                border: `1px solid ${i === sel ? 'var(--color-accent, #0061D0)' : 'var(--color-border, #d8dee6)'}`,
                background: i === sel ? 'var(--color-accent-weak, rgba(0,97,208,.08))' : 'var(--color-bg, #fff)',
                color: 'var(--color-text, #1b2330)',
              }}>
                <span style={{ fontWeight: 600, minWidth: 30, textTransform: 'capitalize' }}>{r.role}</span>
                <span style={{ fontSize: 13, background: 'var(--color-bg-secondary, #f0f3f7)', borderRadius: 4, padding: '1px 6px' }}>
                  {CHANNEL_LABELS[r.channel]}
                </span>
                <span style={{ ...mono, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {describeRecipient(r)}
                </span>
              </button>
              <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove recipient" title="Remove"
                      onClick={() => {
                        patch((t) => { t.recipients.splice(i, 1) })
                        setSel(Math.max(0, Math.min(sel, recipients.length - 2)))
                      }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addRecipient}>
            <Plus size={14} /> Add recipient
          </button>

          {cur && (
            <div style={{
              border: '1px solid var(--color-accent, #0061D0)', borderRadius: 8, padding: 12,
              background: 'var(--color-bg, #fff)', display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                <Field title="Send as" htmlFor="twa-r-role">
                  <select id="twa-r-role" style={input} value={cur.role}
                          onChange={(e) => setRecipient(sel, { ...cur, role: e.target.value as RecipientRole })}>
                    <option value="to">To</option><option value="cc">Cc</option><option value="bcc">Bcc</option>
                  </select>
                </Field>
                <Field title="Channel" htmlFor="twa-r-ch">
                  <select id="twa-r-ch" style={input} value={cur.channel}
                          onChange={(e) => setRecipient(sel, { ...cur, channel: e.target.value as ChannelKey })}>
                    {allowed.map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>)}
                  </select>
                </Field>
              </div>
              <div>
                <span style={labelStyle}>Take the address from</span>
                <Seg ariaLabel="Address source" value={sourceKind(cur)}
                     options={(['lookup', 'column', 'typed', 'role_users', 'record_owner', 'user'] as SourceKind[])
                       .filter((k) => k !== 'lookup' || lookups.length)
                       .map((k) => ({ value: k, label: SOURCE_LABELS[k] }))}
                     onChange={(k) => setSource(sel, k)} />
              </div>
              <RecipientSourceEditor r={cur} lookups={lookups} headerCols={headerCols}
                                     masterCols={masterCols} onChange={(r) => setRecipient(sel, r)} />
            </div>
          )}
        </section>

        {/* ── Channel editors ── */}
        <section style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ borderBottom: '1px solid var(--color-border, #e6eaef)', padding: '0 12px' }}>
            <TabBar ariaLabel="Channel" value={tab}
                    onChange={(c) => { setTab(c); ensureBlock(c); focusRef.current = { kind: 'body' } }}
                    tabs={allowed.map((c) => ({
                      value: c,
                      label: `${CHANNEL_LABELS[c]}${(tpl.channels as Record<string, unknown>)[c] ? '' : ' +'}`,
                    }))} />
          </div>
          {block && (tpl.channels as Record<string, unknown>)[tab] ? (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tab === 'email' && (
                <>
                  <Field title="Subject" htmlFor="twa-subject">
                    <input id="twa-subject" style={input} value={(block as EmailBlock).subject || ''}
                           placeholder="Invoices from {ORG.name} — {COUNT}"
                           {...track((v) => setBlock((b) => { b.subject = v }))}
                           onChange={(e) => { const v = e.target.value; setBlock((b) => { b.subject = v }) }} />
                  </Field>
                  <MessageBodyEditor ref={bodyRef} value={(block as EmailBlock).body}
                                     fields={fields}
                                     onFocus={() => { focusRef.current = { kind: 'body' } }}
                                     onInclude={attachables.length ? addAttachment : undefined}
                                     onChange={(doc: PmDoc) => setBlock((b) => { b.body = doc })} />
                </>
              )}
              {tab === 'whatsapp' && (
                <>
                  <Field title="Approved WhatsApp template (business-initiated)" htmlFor="twa-wa-tpl">
                    <input id="twa-wa-tpl" style={{ ...input, maxWidth: 360 }}
                           value={(block as TextBlock).template_name || ''}
                           placeholder="e.g. invoice_notice_v2"
                           onChange={(e) => { const v = e.target.value; setBlock((b) => { b.template_name = v }) }} />
                  </Field>
                  <div>
                    <span style={labelStyle}>Variables</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {((block as TextBlock).variables || []).map((v, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ ...mono, minWidth: 92 }}>Variable {i + 1} →</span>
                          <input style={{ ...input, flex: 1 }} value={v} aria-label={`Variable ${i + 1}`}
                                 {...track((nv) => setBlock((b) => { (b.variables ||= [])[i] = nv }))}
                                 onChange={(e) => { const nv = e.target.value; setBlock((b) => { (b.variables ||= [])[i] = nv }) }} />
                          <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove variable"
                                  onClick={() => setBlock((b) => { (b.variables ||= []).splice(i, 1) })}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}
                              onClick={() => setBlock((b) => { b.variables = [...(b.variables || []), ''] })}>
                        <Plus size={14} /> Add variable
                      </button>
                    </div>
                  </div>
                </>
              )}
              {tab !== 'email' && (
                <Field title={tab === 'whatsapp' ? 'Text (when no approved template is used)' : 'Text'} htmlFor="twa-text">
                  <textarea id="twa-text" style={textarea} value={(block as TextBlock).text || ''}
                            {...track((v) => setBlock((b) => { b.text = v }))}
                            onChange={(e) => { const v = e.target.value; setBlock((b) => { b.text = v }) }} />
                  {tab === 'sms' && (
                    <div style={{ ...meta, marginTop: 4 }}>
                      ≈ {smsText.length} of {smsPer} characters{smsText.length > smsPer ? ` · ${Math.ceil(smsText.length / (smsGsm ? 153 : 67))} parts` : ''}
                      {' '}· merge fields change the final length · reports and visuals go as a secure link — use {'{LINK}'}
                    </div>
                  )}
                </Field>
              )}
              {tab !== 'sms' && (
                <AttachmentRows channel={tab} block={block as EmailBlock & TextBlock} binding={binding}
                                onChange={(atts) => setBlock((b) => { b.attachments = atts })}
                                onAdd={attachables.length ? addAttachment : undefined} track={track} />
              )}
              <div style={{ display: 'flex' }}>
                <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                        onClick={() => patch((t) => { delete (t.channels as Record<string, unknown>)[tab] })}>
                  Remove {CHANNEL_LABELS[tab]} from this template
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={() => ensureBlock(tab)}>
                <Plus size={14} /> Add {CHANNEL_LABELS[tab]} content
              </button>
            </div>
          )}
        </section>

        {/* ── Fields ── */}
        <aside style={{
          flex: '1 1 250px', maxWidth: 290, padding: '16px 12px', boxSizing: 'border-box',
          borderLeft: '1px solid var(--color-border, #e6eaef)', display: 'flex', flexDirection: 'column', gap: 4,
          overflow: 'auto', maxHeight: 640,
        }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, padding: '0 8px' }}>Fields</h2>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <Search size={14} style={{ position: 'absolute', left: 9, top: 11, color: 'var(--color-text-secondary)' }} />
            <input style={{ ...input, width: '100%', paddingLeft: 28 }} placeholder="Search fields"
                   aria-label="Search fields" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {fieldGroups.map((g) => (
            <div key={g.title}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #687281)', textTransform: 'uppercase',
                letterSpacing: '.04em', padding: '10px 8px 2px',
              }}>{g.title}</div>
              {g.items.map((f) => (
                <button key={f.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(f.key)}
                        style={{
                          display: 'flex', justifyContent: 'space-between', gap: 6, width: '100%', textAlign: 'left',
                          font: 'inherit', fontSize: 13, padding: '5px 8px', border: 0, borderRadius: 4,
                          background: 'transparent', color: 'var(--color-text, #1b2330)', cursor: 'pointer',
                        }}>
                  <span>{f.label}</span>
                  <span style={{ ...mono, color: 'var(--color-text-secondary, #687281)' }}>{f.code}</span>
                </button>
              ))}
            </div>
          ))}
          <div style={{ ...meta, padding: '10px 8px' }}>
            Click a field to insert it. Masked columns show as masked in the message too.
          </div>
        </aside>
      </div>
    </div>
  )
}

function RecipientSourceEditor({ r, lookups, headerCols, masterCols, onChange }: {
  r: RecipientSpec
  lookups: string[]
  headerCols: SendFields['header']
  masterCols: (obj: string) => SendFields['header']
  onChange: (r: RecipientSpec) => void
}) {
  const f = r.from as Record<string, unknown>
  const kind = sourceKind(r)
  if (kind === 'lookup') {
    const obj = String(f.lookup || '')
    const match = (f.match as Record<string, string>) || {}
    const [mcol, mval] = Object.entries(match)[0] || ['', '']
    const recCol = String(mval || '').replace(/[{}]/g, '')
    const cols = masterCols(obj)
    const set = (patch: Record<string, unknown>) => onChange({ ...r, from: { ...(f as object), ...patch } as RecipientSpec['from'] })
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Field title="Master" htmlFor="twa-lk-obj">
          <select id="twa-lk-obj" style={input} value={obj}
                  onChange={(e) => set({ lookup: e.target.value, match: {}, column: '', fallback_column: '' })}>
            {lookups.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <div>
          <span style={labelStyle}>Find the row where</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13 }}>
            <select style={{ ...input, width: 140 }} aria-label="Master column" value={mcol}
                    onChange={(e) => set({ match: e.target.value ? { [e.target.value]: mval || '' } : {} })}>
              <option value="">column…</option>
              {cols.map((c) => <option key={c.key} value={c.db_name}>{c.heading}</option>)}
            </select>
            <span>=</span>
            <select style={{ ...input, width: 150 }} aria-label="Record column" value={recCol}
                    onChange={(e) => set({ match: mcol ? { [mcol]: e.target.value ? `{${e.target.value}}` : '' } : {} })}>
              <option value="">this record's…</option>
              {headerCols.map((c) => <option key={c.key} value={c.key}>{c.heading}</option>)}
            </select>
          </div>
        </div>
        <Field title="Use column" htmlFor="twa-lk-col">
          <select id="twa-lk-col" style={input} value={String(f.column || '')} onChange={(e) => set({ column: e.target.value })}>
            <option value="">choose…</option>
            {cols.map((c) => <option key={c.key} value={c.db_name}>{c.heading}</option>)}
          </select>
        </Field>
        <Field title="If blank, fall back to" htmlFor="twa-lk-fb">
          <select id="twa-lk-fb" style={input} value={String(f.fallback_column || '')}
                  onChange={(e) => set({ fallback_column: e.target.value })}>
            <option value="">(no fallback)</option>
            {cols.map((c) => <option key={c.key} value={c.db_name}>{c.heading}</option>)}
          </select>
        </Field>
        <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={f.multi === 'all'} onChange={(e) => set({ multi: e.target.checked ? 'all' : 'first' })} />
          Every matching row (a master holding a list of contacts)
        </label>
        <div style={{ ...meta, background: 'var(--color-bg-secondary, #f0f3f7)', borderRadius: 6, padding: 8 }}>
          Read through {obj || 'the master'} with the sender's own rights — a row they cannot see is never looked up.
        </div>
      </div>
    )
  }
  if (kind === 'column') {
    return (
      <Field title="Column on this record" htmlFor="twa-r-col">
        <select id="twa-r-col" style={input} value={String(f.column || '')}
                onChange={(e) => onChange({ ...r, from: { column: e.target.value } })}>
          {headerCols.map((c) => <option key={c.key} value={c.key}>{c.heading}</option>)}
        </select>
      </Field>
    )
  }
  if (kind === 'user') {
    return (
      <Field title="Person (their user id or email)" htmlFor="twa-r-user">
        <input id="twa-r-user" style={input} value={String(f.user || '')}
               placeholder="qa.manager@example.com"
               onChange={(e) => onChange({ ...r, from: { user: e.target.value } })} />
        <div style={meta}>Sent to this person's address on record — the way the record's creator is reached.</div>
      </Field>
    )
  }
  if (kind === 'typed') {
    return (
      <Field title="Fixed address" htmlFor="twa-r-fx">
        <input id="twa-r-fx" style={input} value={String(f.typed || '')}
               placeholder={r.channel === 'email' ? 'accounts@example.com' : '+91 98765 43210'}
               onChange={(e) => onChange({ ...r, from: { typed: e.target.value } })} />
      </Field>
    )
  }
  if (kind === 'role_users') {
    return (
      <Field title="Everyone with role" htmlFor="twa-r-ro">
        <input id="twa-r-ro" style={input} value={String(f.role_users || '')} placeholder="e.g. branch-head"
               onChange={(e) => onChange({ ...r, from: { role_users: e.target.value } })} />
      </Field>
    )
  }
  return <div style={meta}>Whoever created each record (their own address on this channel).</div>
}

function AttachmentRows({ channel, block, binding, onChange, onAdd, track }: {
  channel: ChannelKey
  block: EmailBlock & TextBlock
  binding: SendActionBinding
  onChange: (atts: TemplateAttachment[]) => void
  onAdd?: () => void
  track: (set: (v: string) => void) => { onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void }
}) {
  const atts = block.attachments || []
  const set = (i: number, patch: Partial<TemplateAttachment>) =>
    onChange(atts.map((a, j) => (j === i ? { ...a, ...patch } : a)))
  const cell: CSSProperties = { display: 'flex', flexDirection: 'column', minWidth: 0 }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {atts.map((a, i) => {
        const att = (binding.attachables || []).find((x) => x.id === a.ref || x.obj === a.ref)
        const modes = (att?.modes || ['pdf']).filter((m) => channel === 'email' || TEXT_MODES.includes(m))
        return (
          <div key={i} style={{
            border: '1px solid var(--color-border, #d8dee6)', borderRadius: 8, padding: 12,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, alignItems: 'end',
          }}>
            <div style={cell}>
              <span style={labelStyle}>Include</span>
              <select style={input} value={a.ref} onChange={(e) => set(i, { ref: e.target.value })}>
                {(binding.attachables || []).map((x) => (
                  <option key={x.id} value={x.id}>{x.label || x.obj} ({KIND_LABELS[x.kind]})</option>
                ))}
              </select>
            </div>
            <div style={cell}>
              <span style={labelStyle}>As</span>
              <select style={input} value={a.mode === 'document' ? 'pdf' : a.mode}
                      onChange={(e) => set(i, { mode: e.target.value as DeliveryMode })}>
                {modes.map((m) => (
                  <option key={m} value={m}>{channel !== 'email' && m === 'pdf' ? 'Document (PDF)' : MODE_LABELS[m]}</option>
                ))}
              </select>
            </div>
            <div style={cell}>
              <span style={labelStyle}>One per</span>
              <select style={input} value={a.per} onChange={(e) => set(i, { per: e.target.value as 'record' | 'group' })}>
                <option value="record">Record</option>
                <option value="group">Message</option>
              </select>
            </div>
            <div style={cell}>
              <span style={labelStyle}>File name</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <input style={{ ...input, flex: 1 }} value={a.filename || ''} placeholder="Invoice-{invoice_no}.pdf"
                       {...track((v) => set(i, { filename: v }))}
                       onChange={(e) => set(i, { filename: e.target.value })} />
                <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove"
                        onClick={() => onChange(atts.filter((_, j) => j !== i))}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        )
      })}
      {onAdd && (
        <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={onAdd}>
          <Plus size={14} /> Include report / visual / dashboard
        </button>
      )}
    </div>
  )
}

export default TemplateComposer
